/**
 * `Table/BulkBar` (node 12:11248) and the notice that reports what it actually did.
 *
 * Measured: `bg/surface`, `border/default`, `radius/pill`, pl-20/pr-12/py-8, 12px gaps,
 * `0 12px 16px rgba(28,28,28,.18)`; "3 selected" in `Body/14-Med` `text/primary`, "Clear"
 * in `Body/13` `accent/indigo`, a 1×20 `border/strong` divider, then h-32 px-10 `radius/sm`
 * text actions.
 *
 * ===========================================================================
 * FIGMA'S FOUR ACTIONS, AGAINST ONE ENDPOINT THAT DOES TWO THINGS
 * ===========================================================================
 * `POST /countries/:cc/inquiries/bulk` takes `action: 'assign' | 'stage'`. That is the
 * whole surface. So:
 *
 *  · **Move to stage ▾** — built. `lost` is offered but DISABLED, with the reason on the
 *    item: the endpoint refuses it (422 `LOST_REASON_REQUIRED`) because its body carries
 *    no `reason` field, and the API's own message says to use the per-inquiry PATCH so the
 *    reason is recorded against each one. Hiding the item would leave an operator hunting
 *    for a Lost option that is deliberately absent; offering it live would guarantee a
 *    422.
 *  · **Assign developer** — built as "Assign": their own id, `null` to unassign, and — for
 *    a caller who holds `users.manage` — a colleague picker. See the next block; the
 *    design's label says "developer", which is a role this phase still has no way to
 *    enumerate, so the section is labelled "colleague".
 *  · **Export** — omitted. No export route exists in Phase 2 and `reports.export` is a
 *    Phase 5 permission.
 *  · **Delete** — omitted, and this one is not a shrug. There is no delete route for
 *    inquiries anywhere in the API, by design: `inquiries` is an audited spine with an
 *    `events` row per mutation. A Delete button that "worked" would have to be invented
 *    end to end, and a destructive invention is the worst kind.
 *
 * ===========================================================================
 * THE COLLEAGUE PICKER IS PERMISSION-AWARE, AND THAT IS THE WHOLE DESIGN (C5)
 * ===========================================================================
 * `GET /users` is `@RequirePerm('users.manage')`. Per Appendix A
 * (`apps/api/src/seeds/role-matrix.json`) only Country Manager holds it, and a group admin
 * bypasses every `@RequirePerm` (`apps/api/src/common/security/authorize.ts:46`) — so
 * `grp:true` sees the picker too. A Tech Support user holds neither, and for them this menu
 * is byte-for-byte what it was before this feature existed, note and all: showing them a
 * picker fed by a route that will 403 is worse than not showing one, because the failure
 * arrives as a broken menu rather than as an absence they can ask about.
 *
 * This is the same pattern as the Developer's hidden Inquiries nav. No permission grants
 * change; the UI reads the token and renders what the caller can actually reach.
 *
 * Selecting a colleague who is NOT scoped to the current country opens a confirm BEFORE the
 * POST (C6). The prediction behind it is `useColleagues`'s, and it is only a prediction —
 * `BulkResultNotice` below reports the server's `warnings[]` whatever the client guessed.
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, X } from 'lucide-react';
import { BOARD_STAGES, stageTabLabel } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import type { CountryCode } from '../../domain/countries';
import { Alert, Button } from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';
import { MenuItem } from './Toolbar';
import type { BulkAction, BulkRefusal, BulkWarning, BulkWarningCode } from './api';
import { MIN_COLLEAGUE_SEARCH } from '../directory/api';
import type { Colleague, ColleagueDirectory } from '../directory/use-directory';

export function BulkBar({
  count,
  busy,
  canAssign,
  countryCode,
  myUserId,
  colleagues,
  colleagueTerm,
  onColleagueTermChange,
  onClear,
  onMoveToStage,
  onAssign,
}: {
  count: number;
  busy: boolean;
  canAssign: boolean;
  countryCode: CountryCode;
  myUserId: string | null;
  /**
   * `null` when the caller cannot read the user directory — which is NOT the same as an
   * empty one, and is why this is not `readonly Colleague[]`. Null renders the menu
   * exactly as it was before the picker existed (C5).
   */
  colleagues: ColleagueDirectory | null;
  /** The RAW search term — the input's value, so typing never lags behind a timer. */
  colleagueTerm: string;
  onColleagueTermChange: (next: string) => void;
  onClear: () => void;
  onMoveToStage: (stage: InquiryStage) => void;
  onAssign: (userId: string | null) => void;
}): React.JSX.Element | null {
  /**
   * The colleague awaiting "Assign anyway?". Non-null means the confirm is open and NOTHING
   * has been sent — the write happens in the dialog's confirm handler and nowhere else, so
   * cancelling cannot leave a request in flight.
   *
   * Declared above the `count === 0` early return because hooks must be unconditional; the
   * pending confirm is dropped in the same breath, using the render-time adjustment pattern
   * the page uses for its selection. A confirm that survived the selection being cleared
   * would name a count that no longer exists.
   */
  const [pending, setPending] = useState<Colleague | null>(null);
  if (count === 0 && pending !== null) setPending(null);
  if (count === 0) return null;

  /** In scope → send. Out of scope → ask first. This is the gate; there is no other path. */
  const chooseColleague = (person: Colleague): void => {
    if (person.outOfScope) {
      setPending(person);
      return;
    }
    onAssign(person.id);
  };

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      // Bottom-centre, as drawn (the node floats at y=498 over the table). z-40 keeps it
      // under the toast stack at z-50, so an Undo is never hidden behind it.
      className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-pill border border-line bg-surface py-2 pl-5 pr-3 shadow-[0_12px_16px_0_rgba(28,28,28,0.18)]"
    >
      <p className="tnum whitespace-nowrap text-body font-medium text-fg">
        {count} selected
      </p>
      <button
        type="button"
        onClick={onClear}
        className="text-body13 text-indigo hover:underline"
      >
        Clear
      </button>
      <span aria-hidden="true" className="h-5 w-px shrink-0 bg-line-strong" />

      {canAssign ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            disabled={busy}
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-sm px-2.5 text-body font-medium text-fg hover:bg-subtle disabled:opacity-60"
          >
            Assign
            <ChevronDown aria-hidden="true" className="size-3.5" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="center"
              sideOffset={6}
              // 220px is the authored width and stays the width for a caller without the
              // directory (C5). The picker needs room for a name and its scope marker.
              className={`z-50 ${colleagues === null ? 'w-[220px]' : 'w-[264px]'} rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]`}
            >
              {myUserId === null ? null : (
                <MenuItem onSelect={() => onAssign(myUserId)}>Assign to me</MenuItem>
              )}
              <MenuItem onSelect={() => onAssign(null)}>Unassign</MenuItem>

              {colleagues === null ? (
                <p className="px-2 pb-1 pt-2 text-label text-fg-muted">
                  Assigning to a colleague needs the user directory, which arrives with the
                  admin surface.
                </p>
              ) : (
                <ColleagueSection
                  countryCode={countryCode}
                  directory={colleagues}
                  term={colleagueTerm}
                  onTermChange={onColleagueTermChange}
                  onChoose={chooseColleague}
                />
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : null}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          disabled={busy}
          className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-sm px-2.5 text-body font-medium text-fg hover:bg-subtle disabled:opacity-60"
        >
          Move to stage
          <ChevronDown aria-hidden="true" className="size-3.5" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="center"
            sideOffset={6}
            className="z-50 w-[260px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
          >
            {BOARD_STAGES.map((stage) =>
              stage === 'lost' ? (
                <MenuItem key={stage} disabled onSelect={() => undefined}>
                  {stageTabLabel(stage)}
                </MenuItem>
              ) : (
                <MenuItem key={stage} onSelect={() => onMoveToStage(stage)}>
                  {stageTabLabel(stage)}
                </MenuItem>
              ),
            )}
            <p className="px-2 pb-1 pt-2 text-label text-fg-muted">
              {stageTabLabel('lost')} needs a reason per inquiry, so it cannot be set in
              bulk. Move those one at a time.
            </p>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {pending === null ? null : (
        <AssignAnywayDialog
          colleague={pending}
          countryCode={countryCode}
          count={count}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const person = pending;
            setPending(null);
            onAssign(person.id);
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────── the colleague list ── */

/**
 * READ-ONLY, AND ONLY FOR PICKING. No create, no edit, no role display, no search box —
 * `GET /users` is borrowed here to answer one question ("who can I assign this to?"), and
 * anything more would be a user-management surface built inside a bulk menu.
 *
 * The three non-ready states are spelled out rather than collapsing into an empty list. "The
 * directory could not be read" is not "you have no colleagues", and a picker that renders
 * silence after a failed request tells the operator something false.
 */
function ColleagueSection({
  countryCode,
  directory,
  term,
  onTermChange,
  onChoose,
}: {
  countryCode: CountryCode;
  directory: ColleagueDirectory;
  term: string;
  onTermChange: (next: string) => void;
  onChoose: (person: Colleague) => void;
}): React.JSX.Element {
  return (
    <>
      <DropdownMenu.Separator className="my-1.5 h-px bg-line" />
      <DropdownMenu.Label className="px-2 pb-1 text-label font-medium text-fg-muted">
        Assign to a colleague
      </DropdownMenu.Label>

      {/* THE SEARCH BOX (ESC-DIRECTORY). Keystrokes must reach the input, not the
        * menu's type-ahead-to-focus-an-item behaviour, so the wrapper stops
        * DropdownMenu from swallowing them. onKeyDown rather than onKeyDownCapture:
        * Escape and Tab still belong to the menu, and an operator who presses
        * Escape expects the menu to close, not the box to eat it. */}
      <div
        className="px-2 pb-1.5"
        onKeyDown={(event) => {
          if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
        }}
      >
        <input
          type="search"
          value={term}
          autoComplete="off"
          aria-label="Search colleagues"
          placeholder="Search by name or email"
          onChange={(event) => onTermChange(event.target.value)}
          className="w-full rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg placeholder:text-fg-muted"
        />
      </div>

      {directory.status === 'idle' ? (
        <p className="px-2 py-1.5 text-body13 text-fg-muted">
          Type at least {MIN_COLLEAGUE_SEARCH} characters to find a colleague.
        </p>
      ) : directory.status === 'loading' ? (
        <p className="px-2 py-1.5 text-body13 text-fg-muted">Searching…</p>
      ) : directory.status === 'error' ? (
        <p className="px-2 py-1.5 text-body13 text-fg-muted">
          The user directory could not be read, so this list is not the whole story. Assign
          to yourself for now, or reopen this menu to try again.
        </p>
      ) : directory.people.length === 0 ? (
        <p className="px-2 py-1.5 text-body13 text-fg-muted">
          No active account matches that.
        </p>
      ) : (
        <>
          {/* A menu, not a page: past ~7 rows this scrolls rather than growing off-screen. */}
          <div className="max-h-[228px] overflow-y-auto">
            {directory.people.map((person) => (
              <MenuItem key={person.id} onSelect={() => onChoose(person)}>
                <span className="flex min-w-0 items-center gap-2">
                  {/* 2.5b: the picker shows the same disc — photo when present —
                   * as every owner column, so "which Sam?" is answered by the
                   * face before the assignment, not after it. */}
                  <UserAvatar name={person.name} userId={person.id} size={24} decorative />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{person.name}</span>
                    {person.outOfScope ? (
                      // Words, not just a colour — the marker is the reason the confirm is
                      // about to appear, and it has to survive a greyscale screenshot.
                      <span className="text-label text-warn-fg">Not in {countryCode}</span>
                    ) : null}
                  </span>
                </span>
              </MenuItem>
            ))}
          </div>
          {directory.capped ? (
            // RULE 11: a truncated list that does not say it is truncated is a
            // silent failure — the operator concludes a colleague has no account
            // when the search simply stopped counting. `total` is the server's
            // count for this term; `people.length` is what is on screen.
            <p className="px-2 pb-1 pt-1.5 text-label text-fg-muted">
              Showing the first {directory.people.length} of {directory.total} matches. Type
              more to narrow it.
            </p>
          ) : null}
        </>
      )}
    </>
  );
}

/* ────────────────────────────────────────────────────────── assign anyway? ── */

/**
 * "AO", "AO and TZ", "AO, TZ and CD" — an Oxford-comma-free list of country codes.
 *
 * Written with `pop()` rather than index arithmetic because `noUncheckedIndexedAccess` is
 * on: `codes[codes.length - 1]` is `CountryCode | undefined` and the only honest ways past
 * that are a cast or this. The cast would be the third-last thing anybody checked.
 *
 * Deliberately NOT exported. It is copy, not contract, and `assign-colleague.test.tsx`
 * asserts it through the sentence the operator actually reads — which is the claim worth
 * pinning anyway. Exporting it would also add a `react-refresh/only-export-components`
 * warning to a file that already carries one.
 */
function countryList(codes: readonly CountryCode[]): string {
  const rest = [...codes];
  const last = rest.pop();
  if (last === undefined) return '';
  return rest.length === 0 ? last : `${rest.join(', ')} and ${last}`;
}

/**
 * The pre-write confirm (C6).
 *
 * ===========================================================================
 * IT ASKS BEFORE THE POST, AND CANCEL SENDS NOTHING
 * ===========================================================================
 * The server accepts this assignment — that is the point. `warnings[]` is an advisory about
 * a write that SUCCEEDED, so by the time it arrives the work is already parked with somebody
 * who cannot see it and the operator's only recourse is a second bulk action. Asking first
 * is the only version of this that prevents anything.
 *
 * Which makes the Cancel path the load-bearing one: it clears the pending colleague and
 * returns, and there is no other route from the picker to `onAssign` for an out-of-scope
 * person. `assign-colleague.test.tsx` asserts that by counting calls to the bulk mutation
 * itself, through the page's own wiring, because a confirm that renders but does not gate is
 * exactly the false green this repo keeps catching. That assertion was checked by BREAKING
 * the gate (dropping the `return` below) and confirming it goes red.
 *
 * The copy names all four things the ruling asks for: the person, the country, how many
 * inquiries, and the consequence.
 */
function AssignAnywayDialog({
  colleague,
  countryCode,
  count,
  busy,
  onConfirm,
  onCancel,
}: {
  colleague: Colleague;
  countryCode: CountryCode;
  count: number;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const subject = count === 1 ? '1 inquiry' : `${String(count)} inquiries`;
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        // Escape and the overlay are Cancel. There is no dismissal that assigns.
        if (!next) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(460px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-surface p-6 shadow-[var(--crf-shadow-overlay)]">
          <Dialog.Title className="text-h3 text-fg">Assign anyway?</Dialog.Title>
          <Dialog.Description className="mt-1 text-label text-fg-muted">
            {colleague.name} has no role in {countryCode}.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-2 text-body13 text-fg">
            <p>
              Assigning {subject} to {colleague.name} will be accepted, but they will not be
              able to see this work: the pipeline is scoped by country, and {countryCode} is
              not one of theirs.
            </p>
            <p className="text-fg-secondary">
              {colleague.countries.length === 0
                ? `${colleague.name} holds no country roles at all, so these inquiries would sit with somebody who cannot open any of them.`
                : `${colleague.name} works in ${countryList(colleague.countries)}. Ask an administrator for a ${countryCode} role first if this is meant to be their work.`}
            </p>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={onConfirm} disabled={busy}>
              {busy ? 'Assigning…' : `Assign ${subject} anyway`}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ─────────────────────────────────────────────────────── the honest report ── */

const REFUSAL_TEXT: Readonly<Record<string, string>> = {
  COUNTRY_SCOPE_VIOLATION: 'belongs to another country',
  NOT_FOUND: 'no longer exists',
};

export function refusalReason(code: string): string {
  return REFUSAL_TEXT[code] ?? `was refused (${code})`;
}

/**
 * The warning vocabulary, keyed by the API's `WarningCode` — EXHAUSTIVELY.
 *
 * `Record<BulkWarningCode, …>` means adding a member to the shared enum breaks this build
 * until somebody writes the sentence for it, which is the point: a warning the server can
 * send and this client renders as a bare code is a warning nobody acts on.
 *
 * `refused` and `warnings` share a shape but never a vocabulary (owner ruling 3), so this
 * map and `REFUSAL_TEXT` are separate and neither falls through to the other. A 200 advisory
 * that could be rendered by the refusal renderer is precisely the confusion the split
 * exists to prevent.
 */
const WARNING_TEXT: Readonly<Record<BulkWarningCode, string>> = {
  ASSIGNEE_OUT_OF_SCOPE: 'is assigned to somebody with no role in this country',
};

/**
 * Widened to `string` on purpose. The type says the enum is closed; the wire does not have
 * to agree with this build, and an unseen code is still information — echoing it beats
 * "something happened", which cannot be diagnosed from a screenshot.
 */
const WARNING_LOOKUP: Readonly<Record<string, string>> = WARNING_TEXT;

/**
 * Module-private, unlike its `refusalReason` sibling: nothing outside this file renders a
 * warning, and `assign-colleague.test.tsx` covers it through `BulkResultNotice` — including
 * a loop over every member of the shared `WarningCode` enum, which is the exhaustiveness
 * claim that matters.
 */
function warningReason(code: string): string {
  return WARNING_LOOKUP[code] ?? `was flagged (${code})`;
}

export interface BulkOutcome {
  action: BulkAction;
  requested: number;
  updated: string[];
  refused: BulkRefusal[];
  /**
   * OPTIONAL because the API omits the key entirely when there is nothing to warn about
   * (owner ruling 3 / C2). Every read here is `?? []`.
   */
  warnings?: BulkWarning[];
}

/**
 * What the bulk call did — including what it refused, and what it accepted with a warning.
 *
 * ===========================================================================
 * A PARTIAL SUCCESS IS NOT A SUCCESS TOAST
 * ===========================================================================
 * `POST …/bulk` is per-id authorised and returns `{updated, refused:[{id,code}], warnings?}`.
 * Three facts make an unqualified "5 inquiries updated" a lie:
 *
 *  1. **`refused` can be non-empty.** A selection can straddle a country the operator may
 *    not touch, or contain a row somebody else has since removed. Those ids were NOT
 *    changed, and a green toast over a half-applied batch is precisely the false-success
 *    this project's gates exist to catch. So any refusal renders a persistent WARNING
 *    panel listing every refused id and why — not a toast, because a toast disappears and
 *    this needs to still be on screen when the operator looks again.
 *  2. **`updated` is an END-STATE claim, not a write count.** The service says so: a row
 *    that already held the requested value is a no-op and appears in `updated` without
 *    having been written or evented. The wording is therefore "now …" rather than
 *    "changed", and the number is stated against what was requested ("4 of 5") so the
 *    arithmetic is visible instead of implied.
 *  3. **`warnings` can be non-empty on a batch where nothing was refused.** Every id
 *    succeeded and the result is still not what the operator wanted: the work is now parked
 *    with somebody who cannot see it. The picker's confirm (C6) tries to ask first, but the
 *    server is the authority and its answer is surfaced HERE, in this same panel, whether
 *    or not the client predicted it — a warning the client did not expect is the one most
 *    worth showing. Same reasoning as refusals, so the same panel rather than a second one:
 *    it persists, it names every id, and it keeps the batch from reading as clean.
 */
export function BulkResultNotice({
  outcome,
  onDismiss,
}: {
  outcome: BulkOutcome;
  onDismiss: () => void;
}): React.JSX.Element {
  const { action, requested, updated, refused } = outcome;
  const warnings = outcome.warnings ?? [];
  const verb = action === 'stage' ? 'in the requested stage' : 'assigned as requested';
  const partial = refused.length > 0;
  const flagged = warnings.length > 0;

  return (
    <Alert
      tone={partial || flagged ? 'warning' : 'info'}
      title={
        `${String(updated.length)} of ${String(requested)} inquiries are now ${verb}` +
        (partial ? ` — ${String(refused.length)} refused` : '') +
        (flagged ? ` — ${String(warnings.length)} assigned outside this country` : '')
      }
    >
      <div className="flex flex-col gap-1.5">
        {partial ? (
          <ul className="flex flex-col gap-0.5">
            {refused.map((refusal) => (
              <li key={refusal.id} className="tnum">
                #{refusal.id} — {refusalReason(refusal.code)}
              </li>
            ))}
          </ul>
        ) : null}
        {flagged ? (
          <div className="flex flex-col gap-0.5">
            <p className="font-medium">Assigned, but outside this country</p>
            <ul className="flex flex-col gap-0.5">
              {warnings.map((warning) => (
                <li key={warning.id} className="tnum">
                  #{warning.id} — {warningReason(warning.code)}
                </li>
              ))}
            </ul>
            <p className="text-fg-secondary">
              These were assigned as requested — nothing about them was refused. The
              assignee holds no role here, so the work will not appear in their pipeline
              until somebody gives them one.
            </p>
          </div>
        ) : null}
        <p className="text-fg-secondary">
          Inquiries that already held the requested value are counted here without having
          been changed. The audit trail records only the real changes.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex w-fit items-center gap-1 text-label font-medium underline"
        >
          <X aria-hidden="true" className="size-3" />
          Dismiss
        </button>
      </div>
    </Alert>
  );
}
